/**
 * Pure model for the Criteria tree tab (E1 §1.3–§1.6): the prototype's layout
 * algorithm (`brainVals`, ported line for line), the node-box view model,
 * detail-panel labels, and the new-node form (resolution, placement, "when"
 * answers, the 8 validation rules and the API payload).
 *
 * Input is the `GET /api/wiki/brain/tree` payload (contracts/BE-brain.md §2).
 * No React, no I/O — everything here is unit-tested in __tests__.
 */
import { BRAIN_NEW_COPY, BRAIN_NEW_MESSAGES, BRAIN_DETAIL } from './brainCopy';

export const BUILT_IN_BRANCH_ID = 'wiki';

/** Layout constants (prototype `colX`, `colW`, `ROW`, node height `H`). */
export const BRAIN_LAYOUT = Object.freeze({
    colX: Object.freeze([14, 150, 412, 686, 944, 1202, 1460]),
    colW: Object.freeze([118, 236, 252, 236, 236, 236, 236]),
    row: 42,
    nodeH: 32,
    groupGap: 16,
    top: 14,
});

// `paused` = a tenant check held while the Brain runs the base tree only (contract §2.1 `mode`).
const SLATE_STATUSES = new Set(['draft', 'testing', 'tested', 'failed', 'paused']);

export const isRootNode = (node) => node?.kind === 'root';
export const isGroupNode = (node) => node?.kind === 'group';
export const isCheckNode = (node) => Boolean(node) && !isRootNode(node) && !isGroupNode(node);
/** A pending proposal rendered as a dashed node (contract: id `p-…`, status `proposed`). */
export const isProposalNode = (node) => node?.status === 'proposed';
/** A proposal node whose server replay failed (prototype `n.failed`). */
export const isFailedProposal = (node) => isProposalNode(node) && Boolean(node?.proposal?.failed);
/** Tenant draft lifecycle (prototype `isDraft` + the rebuild's `failed` / `paused` states). */
export const isSlateNode = (node) => SLATE_STATUSES.has(node?.status);

/** Index the payload's nodes by id. */
export function indexBrainNodes(nodes) {
    const byId = {};
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
        if (node && node.id != null) byId[node.id] = node;
    });
    return byId;
}

/**
 * The branch a node belongs to: its own `branch_id` (the server annotates every
 * node), else the prototype's walk up the parents to the group under the root.
 */
export function brainBranchOf(node, byId) {
    if (!node) return BUILT_IN_BRANCH_ID;
    if (node.branch_id) return node.branch_id;
    let current = node;
    const seen = new Set();
    while (current && !current.branch_id && current.parent_id && !seen.has(current.id)) {
        seen.add(current.id);
        const parent = byId[current.parent_id];
        if (!parent || isRootNode(parent)) break;
        current = parent;
    }
    return (current && current.branch_id) || BUILT_IN_BRANCH_ID;
}

/** Root + every node of `branchId`, in payload (= prototype kids) order. */
export function brainNodesForBranch(nodes, branchId, byId = indexBrainNodes(nodes)) {
    const branch = branchId || BUILT_IN_BRANCH_ID;
    return (Array.isArray(nodes) ? nodes : []).filter(
        (node) => isRootNode(node) || brainBranchOf(node, byId) === branch,
    );
}

/** Edge tone for the link INTO `node` (prototype edge stroke rules). */
export function brainEdgeTone(node) {
    if (isFailedProposal(node)) return 'failed';
    if (node?.proposal) return 'proposal';
    if (isSlateNode(node)) return 'draft';
    return 'normal';
}

/**
 * The prototype tree layout (E1 §1.3.5), exactly:
 *   groups under the root are centred on their children; every other node takes
 *   a 42px row; a group block ends with a 16px gap; the root is centred on the
 *   groups. Returns `{ pos: {id: {x, y, w}}, width, height, edges }`.
 *   `nodes` = the branch's nodes (root first is not required).
 */
export function layoutBrainTree(nodes) {
    const list = Array.isArray(nodes) ? nodes : [];
    const { colX, colW, row: ROW, nodeH: H, groupGap, top } = BRAIN_LAYOUT;
    const root = list.find(isRootNode);
    const rootId = root ? root.id : 'root';
    const kidsOf = new Map();
    list.forEach((node) => {
        const parent = node.parent_id;
        if (parent == null) return;
        if (!kidsOf.has(parent)) kidsOf.set(parent, []);
        kidsOf.get(parent).push(node);
    });
    const kids = (id) => kidsOf.get(id) || [];
    const pos = {};
    let y = top;
    const placing = new Set();

    const place = (node, depth) => {
        if (placing.has(node.id)) return y; // cycle guard (never in a valid tree)
        placing.add(node.id);
        const ks = kids(node.id);
        if (depth === 1) {
            const first = y;
            let last = y;
            ks.forEach((k) => { last = place(k, 2); });
            if (!ks.length) y += ROW;
            pos[node.id] = { x: colX[1], y: Math.round((first + last) / 2), w: colW[1] };
            y += groupGap;
            return first;
        }
        const me = y;
        const col = Math.min(depth, 6);
        pos[node.id] = { x: colX[col], y: me, w: colW[col] };
        if (ks.length) {
            ks.forEach((k) => place(k, depth + 1));
            y = Math.max(y, me + ROW);
        } else {
            y += ROW;
        }
        return me;
    };

    const groups = kids(rootId);
    groups.forEach((group) => place(group, 1));
    if (!groups.length) y += ROW;
    const groupYs = groups.map((group) => pos[group.id].y);
    pos[rootId] = {
        x: colX[0],
        y: groupYs.length ? Math.round((Math.min(...groupYs) + Math.max(...groupYs)) / 2) : top,
        w: colW[0],
    };

    let maxRight = 0;
    Object.keys(pos).forEach((id) => { maxRight = Math.max(maxRight, pos[id].x + pos[id].w); });
    const width = maxRight + 16;
    const height = y + 4;

    const edges = list
        .filter((node) => node.parent_id != null && pos[node.id] && pos[node.parent_id])
        .map((node) => {
            const a = pos[node.parent_id];
            const b = pos[node.id];
            const x1 = a.x + a.w;
            const y1 = a.y + H / 2;
            const x2 = b.x;
            const y2 = b.y + H / 2;
            const xm = Math.round((x1 + x2) / 2);
            const tone = brainEdgeTone(node);
            return {
                id: node.id,
                parentId: node.parent_id,
                d: `M ${x1} ${y1} C ${xm} ${y1}, ${xm} ${y2}, ${x2} ${y2}`,
                tone,
                dashed: Boolean(node.proposal) || isSlateNode(node),
            };
        });

    return { pos, width, height, edges };
}

/** Pass-rate tone (prototype `passCol`). */
export function brainPassTone(pass) {
    if (pass == null) return 'none';
    if (pass >= 90) return 'good';
    if (pass >= 75) return 'mid';
    return 'bad';
}

/** Pass dot size from 30-day fires (prototype `sz`). */
export function brainDotSize(fires) {
    const n = Number(fires) || 0;
    if (n >= 4000) return 10;
    if (n >= 500) return 8;
    return 7;
}

/** Node box variant (E1 §1.3.6, first match wins). */
export function brainNodeVariant(node) {
    if (isRootNode(node)) return 'root';
    if (isGroupNode(node)) return 'group';
    if (isFailedProposal(node)) return 'failed';
    if (isProposalNode(node)) return 'proposal';
    if (isSlateNode(node)) return 'draft';
    return 'normal';
}

/** The small status chip on a node box: draft / testing / tested (prototype) + failed / paused / off (rebuild). */
export function brainNodeStatusChip(node, { testing = false } = {}) {
    if (!node || isRootNode(node)) return null;
    if (testing) return 'testing';
    const status = node.status;
    if (SLATE_STATUSES.has(status)) return status;
    if (status === 'off') return 'off';
    return null;
}

/**
 * Everything a node box renders, from the node + context.
 * `ctx` = `{ canManage, testing, childCount }`.
 */
export function brainNodeView(node, ctx = {}) {
    const { canManage = false, testing = false, childCount = 0 } = ctx;
    const stats = node?.stats || {};
    const check = isCheckNode(node);
    const effectiveStatus = testing ? 'testing' : node?.status;
    const slate = SLATE_STATUSES.has(effectiveStatus);
    const hasProposal = Boolean(node?.proposal);
    return {
        variant: testing && check && !isProposalNode(node) ? 'draft' : brainNodeVariant(node),
        groupDraft: isGroupNode(node) && (node.status === 'draft' || node.status === 'paused'),
        weakBorder: Boolean(stats.weak),
        isCheck: check,
        dotSize: brainDotSize(stats.fires_30d),
        dotTone: brainPassTone(stats.pass),
        when: node?.when || null,
        role: isGroupNode(node) && node.role ? node.role : null,
        childCount: isGroupNode(node) ? childCount : 0,
        learned: node?.origin === 'learned' && !hasProposal,
        admin: node?.origin === 'admin' && !slate,
        fixed: Boolean(node?.fixed),
        weak: Boolean(stats.weak) && !hasProposal,
        pendingVersion: !isProposalNode(node) && node?.pending_version_label ? node.pending_version_label : null,
        statusChip: brainNodeStatusChip(node, { testing }),
        off: node?.status === 'off',
        canChild: Boolean(canManage) && Boolean(node?.can_child) && !isProposalNode(node),
    };
}

/** Labels root → node joined " › " (prototype `brSelPath`). */
export function brainNodePath(node, byId) {
    const labels = [];
    const seen = new Set();
    let current = node;
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        labels.unshift(current.label || '');
        current = current.parent_id != null ? byId[current.parent_id] : null;
    }
    return labels.join(' › ');
}

/** Detail status chip `{label, tone}` (prototype `brSelStatus` + server `status_label`). */
export function brainStatusChip(node, { testing = false } = {}) {
    if (!node) return null;
    if (testing) return { label: 'Replay test running', tone: 'slate' };
    const status = node.status;
    const label = node.status_label || 'Active';
    if (status === 'draft' || status === 'testing' || status === 'tested' || status === 'paused') {
        return { label, tone: 'slate' };
    }
    if (status === 'failed' || isFailedProposal(node)) return { label, tone: 'red' };
    if (status === 'proposed') return { label, tone: 'amber' };
    if (status === 'off') return { label, tone: 'grey' };
    return { label, tone: 'green' };
}

/** Detail origin chip: groups say "Group" (prototype). */
export function brainOriginChip(node) {
    if (!node) return null;
    if (isGroupNode(node)) return { label: 'Group', tone: 'grey' };
    if (isRootNode(node)) return { label: node.origin_label || 'Base tree', tone: 'grey' };
    if (node.origin === 'learned') return { label: node.origin_label || 'Learned', tone: 'violet' };
    if (node.origin === 'admin') return { label: node.origin_label || 'Written by an admin', tone: 'sky' };
    return { label: node.origin_label || 'Base tree', tone: 'grey' };
}

/** "JEV heart" / "JEV brain" / "JEV driver" (groups only). */
export function brainRoleChipLabel(role) {
    return role ? `JEV ${String(role).toLowerCase()}` : '';
}

/** Replay-tests chip. */
export function brainPassChip(pass) {
    if (pass == null) return { label: BRAIN_DETAIL.notTested, tone: 'grey' };
    const tone = brainPassTone(pass);
    return {
        label: `${pass}% in replay tests`,
        tone: tone === 'good' ? 'green' : tone === 'mid' ? 'amber' : 'red',
    };
}

/** Usage line: "4,812 times in 30 days" / "Hasn't run yet". */
export function brainFiresLabel(fires) {
    const n = Number(fires) || 0;
    return n > 0 ? `${n.toLocaleString('en-US')} times in 30 days` : BRAIN_DETAIL.notRun;
}

/** Number with thousands separators (stats tiles). */
export function brainNumber(value) {
    const n = Number(value) || 0;
    return n.toLocaleString('en-US');
}

/** BUILD THE TREE add-child button label. */
export function brainAddChildLabel(node) {
    if (isRootNode(node)) return 'Add a group';
    if (isGroupNode(node)) return 'Add a check to this group';
    return 'Add a child check';
}

/** BUILD THE TREE branch-out button label. */
export function brainBranchOutLabel(node) {
    return isGroupNode(node) ? 'Add a group next to it' : 'Branch out next to it';
}

/**
 * Can this node be switched on from the panel? Never while it is `paused`
 * (the Brain runs the base tree only), and never a tenant node while the tree
 * `mode` is paused — the server would refuse with `mode.message`.
 */
export function brainCanActivate(node, { modePaused = false } = {}) {
    if (!node || node.status === 'paused') return false;
    if (modePaused && node.origin !== 'base') return false;
    return true;
}

/** Brain mode from the tree payload (contract §2.1): `{paused, message}`. */
export function brainMode(data) {
    const mode = data && typeof data.mode === 'object' && data.mode ? data.mode : {};
    const message = typeof mode.message === 'string' && mode.message.trim() ? mode.message.trim() : null;
    return { value: mode.value || 'learning', paused: Boolean(mode.paused), message };
}

/** Prototype `brSelCanSibling` (the UI also needs manage). */
export function brainCanSibling(node) {
    return Boolean(node) && !isRootNode(node) && !isProposalNode(node);
}

// ─── Tab model ────────────────────────────────────────────────────────────

/**
 * Everything the tree tab needs from the payload for the selected branch.
 * Falls back to the built-in branch when `branchId` no longer exists.
 */
export function buildBrainTreeModel(data, branchId) {
    const nodes = Array.isArray(data?.nodes) ? data.nodes : [];
    const branches = Array.isArray(data?.branches) && data.branches.length
        ? data.branches
        : [{ id: BUILT_IN_BRANCH_ID, name: 'Ask the Wiki', built_in: true, status: 'active', check_count: 0 }];
    const branch = branches.find((b) => b.id === branchId)
        || branches.find((b) => b.id === BUILT_IN_BRANCH_ID)
        || branches[0];
    const byId = indexBrainNodes(nodes);
    const branchNodes = brainNodesForBranch(nodes, branch.id, byId);
    const childCount = {};
    branchNodes.forEach((node) => {
        if (node.parent_id != null) childCount[node.parent_id] = (childCount[node.parent_id] || 0) + 1;
    });
    const layout = layoutBrainTree(branchNodes);
    const stats = (data?.stats && data.stats[branch.id]) || computeBrainStats(branchNodes);
    return { nodes, byId, branches, branch, branchNodes, childCount, layout, stats, mode: brainMode(data) };
}

/** Client fallback for `stats.<branch>` (prototype `brStats`). */
export function computeBrainStats(branchNodes) {
    const list = Array.isArray(branchNodes) ? branchNodes : [];
    const checks = list.filter(isCheckNode);
    return {
        checks: checks.length,
        fired_today: list.reduce((sum, node) => sum + (Number(node?.stats?.fires_today) || 0), 0),
        learned: checks.filter((node) => node.origin === 'learned' && !isProposalNode(node)).length,
        proposed: list.filter((node) => Boolean(node.proposal)).length,
        weak: checks.filter((node) => Boolean(node?.stats?.weak)).length,
    };
}

/** Branch chip meta: "26 checks" / "0 checks · draft". */
export function brainBranchMeta(branch) {
    const n = Number(branch?.check_count) || 0;
    const count = `${n} ${n === 1 ? 'check' : 'checks'}`;
    if (branch?.built_in || branch?.status === 'active') return count;
    return `${count} · ${branch?.status || 'draft'}`;
}

// ─── New-node modal (`brOpenNew` / `brNewVals` / `brSaveNew`) ──────────────

export const BRAIN_ANSWER_TYPES = Object.freeze([
    Object.freeze({ id: 'yes_no', label: 'Yes / No' }),
    Object.freeze({ id: 'pick_one', label: 'Pick one' }),
    Object.freeze({ id: 'score', label: 'Score 0–4' }),
]);

export const BRAIN_EFFECTS = Object.freeze([
    "Add guidance to the writer's brief",
    'Search a category first',
    'Offer the page owner',
    'Send the draft back for a rewrite',
    'Hold the answer for review',
]);

export const BRAIN_MAX_OPTIONS = 12;

const blankOption = () => ({ label: '', what: '', not: '' });

/** A fresh form (prototype `brOpenNew` reset). */
export function brainNewForm(mode, anchorId) {
    return {
        mode,
        anchorId: anchorId || 'root',
        question: '',
        type: 'yes_no',
        options: [blankOption(), blankOption()],
        levels: ['', '', '', '', ''],
        yes: '',
        no: '',
        effect: BRAIN_EFFECTS[0],
        when: 'Always',
        name: '',
        description: '',
    };
}

/** 'branch' | 'group' | 'check' — group when mode=group, child of the root, or sibling of a group. */
export function brainNewKind(mode, anchor) {
    if (mode === 'branch') return 'branch';
    if (mode === 'group') return 'group';
    if (mode === 'child' && isRootNode(anchor)) return 'group';
    if (mode === 'sibling' && isGroupNode(anchor)) return 'group';
    return 'check';
}

export function brainNewTitle(mode, kind) {
    const { titles } = BRAIN_NEW_COPY;
    if (kind === 'branch') return titles.branch;
    if (kind === 'group') return titles.group;
    return mode === 'child' ? titles.child : titles.sibling;
}

export function brainNewSaveLabel(kind) {
    return BRAIN_NEW_COPY.save[kind] || BRAIN_NEW_COPY.save.check;
}

/** The placement bar under the modal title. */
export function brainNewPlacement({ mode, kind, anchor, byId, branchName }) {
    if (kind === 'branch') return BRAIN_NEW_COPY.branchPlacement;
    if (kind === 'group') return `A new group under the Router, in the ${branchName || 'Ask the Wiki'} branch.`;
    if (mode === 'child') return `Under: ${brainNodePath(anchor, byId)}`;
    const parent = anchor && anchor.parent_id != null ? byId[anchor.parent_id] : null;
    return `Next to: ${anchor?.label || ''}  ·  under ${(parent && parent.label) || 'Router'}`;
}

/**
 * "Ask this when the parent's answer is" chips: `Always` + the parent check's
 * answers minus Other. Only for a child of a check; a dynamic-subcategory
 * parent has none (the field hides when only "Always" is left).
 */
export function brainWhenOptions(mode, kind, anchor) {
    if (kind !== 'check' || mode !== 'child' || !isCheckNode(anchor)) return [];
    let base = [];
    if (anchor.dynamic === 'subcategory') base = [];
    else if (anchor.dynamic === 'category') base = (anchor.options || []).map((o) => o.label);
    else if (anchor.type === 'yes_no') base = ['Yes', 'No'];
    else if (anchor.type === 'score') base = ['2 or more', '3 or more', '4'];
    else base = (anchor.options || []).map((o) => o.label);
    const answers = base.filter((label) => label && label !== 'Other');
    return answers.length ? ['Always', ...answers] : [];
}

/**
 * Counting / maths / date phrases JEV handles poorly — the prototype's list
 * with word boundaries (so "account" / "discount" don't match "count"), kept in
 * step with the server (services/brain/validator.py `_WEAK_PHRASES`).
 */
const WEAK_PHRASES = [
    'how many',
    'count(?:s|ed|ing)?',
    'calculat(?:e|es|ed|ing|ion|ions)',
    'sum of',
    'sum up',
    'add up',
    'adds up',
    'total of',
    'subtract(?:s|ed|ing)?',
    'multiply|multiplied|multiplying',
    'divide|divided|dividing',
    'average of',
    'percentage of',
    'before \\d+',
    'after \\d+',
    'older than',
    'newer than',
    'earlier than',
    'later than',
    'days? (?:between|since|until|ago)',
    '(?:more|less|fewer|greater) than \\d+',
];
export const BRAIN_WEAK_QUESTION = new RegExp(`\\b(?:${WEAK_PHRASES.join('|')})\\b`, 'i');

const trim = (value) => (typeof value === 'string' ? value.trim() : '');

/** Pick-one rows with a label (a typed "Other" is dropped: it is always added last). */
export function brainFilledOptions(options) {
    return (Array.isArray(options) ? options : []).filter((o) => {
        const label = trim(o?.label);
        return label && label.toLowerCase() !== 'other';
    });
}

/** First failing rule's verbatim message, or null (prototype `brSaveNew`). */
export function validateBrainNewForm(form, kind) {
    const M = BRAIN_NEW_MESSAGES;
    if (kind === 'branch') return trim(form?.name) ? null : M.branchName;
    if (kind === 'group') return trim(form?.name) ? null : M.groupName;
    const question = trim(form?.question);
    if (question.length < 8) return M.question;
    if (BRAIN_WEAK_QUESTION.test(question)) return M.weak;
    if (form.type === 'pick_one') {
        const filled = brainFilledOptions(form.options);
        if (filled.length < 2) return M.twoOptions;
        if (filled.some((o) => !trim(o.what) || !trim(o.not))) return M.optionMeaning;
        return null;
    }
    if (form.type === 'score') {
        const levels = form.levels || [];
        if (!trim(levels[0]) || !trim(levels[4])) return M.levels;
        return null;
    }
    if (!trim(form.yes)) return M.yes;
    return null;
}

/**
 * The request for the form: `{ path, body }`.
 *   branch → POST /wiki/brain/branches {name, description}
 *   group  → POST /wiki/brain/nodes {mode, anchor_id, branch_id, name, description}
 *   check  → POST /wiki/brain/nodes {mode, anchor_id, branch_id, question, type, effect, when, + type fields}
 */
export function brainNewRequest(form, kind, { branchId = BUILT_IN_BRANCH_ID } = {}) {
    if (kind === 'branch') {
        return {
            path: '/wiki/brain/branches',
            body: { name: trim(form.name), description: trim(form.description) },
        };
    }
    const base = { mode: form.mode, anchor_id: form.anchorId, branch_id: branchId || BUILT_IN_BRANCH_ID };
    if (kind === 'group') {
        return {
            path: '/wiki/brain/nodes',
            body: { ...base, name: trim(form.name), description: trim(form.description) },
        };
    }
    const body = {
        ...base,
        question: trim(form.question),
        type: form.type,
        effect: form.effect || BRAIN_EFFECTS[0],
        when: form.mode === 'child' && form.when && form.when !== 'Always' ? form.when : null,
    };
    if (form.type === 'pick_one') {
        body.options = brainFilledOptions(form.options).map((o) => ({
            label: trim(o.label),
            description: trim(o.what),
            not_for: trim(o.not),
        }));
    } else if (form.type === 'score') {
        body.levels = [0, 1, 2, 3, 4].map((i) => trim((form.levels || [])[i]));
    } else {
        body.yes = trim(form.yes);
        body.no = trim(form.no);
    }
    return { path: '/wiki/brain/nodes', body };
}
